import { useCallback, useEffect, useLayoutEffect, useState, lazy, Suspense } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Globe, MapPin, Sparkles, Shield, Zap, Camera, Video, Map, X, Mail } from 'lucide-react';
import Header from './components/Header';
import Footer from './components/Footer';
import FeatureCard from './components/FeatureCard';
import AppStoreBadges from './components/AppStoreBadges';
import StructuredData from './components/StructuredData';
import ResponsiveImage from './components/ResponsiveImage';
import { LANGUAGE_STORAGE_KEY, rtlLanguages, supportedLanguages } from './i18n/config';

// Lazy load heavy components that are below the fold
const FAQ = lazy(() => import('./components/FAQ'));
const VideoIdeas = lazy(() => import('./components/VideoIdeas'));

const THEME_STORAGE_KEY = 'tripreel-theme';

const getSystemPreference = () => {
  if (typeof window === 'undefined') {
    return 'light';
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

const getInitialTheme = () => {
  if (typeof window === 'undefined') {
    return 'light';
  }

  if (typeof window.__INITIAL_THEME__ === 'string') {
    return window.__INITIAL_THEME__;
  }

  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') {
      return stored;
    }
  } catch (error) {
    return getSystemPreference();
  }

  return getSystemPreference();
};

function App() {
  const { t, i18n } = useTranslation();
  const { lang } = useParams();
  const isRTL = rtlLanguages.includes(i18n.language);
  const [theme, setTheme] = useState(getInitialTheme);
  const [hasManualPreference, setHasManualPreference] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    if (typeof window.__HAS_MANUAL_THEME__ === 'boolean') {
      return window.__HAS_MANUAL_THEME__;
    }

    try {
      const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
      return stored === 'light' || stored === 'dark';
    } catch (error) {
      return false;
    }
  });
  const [showContactPopup, setShowContactPopup] = useState(() => {
    if (typeof window === 'undefined') return false;
    const redirectPath = sessionStorage.getItem('redirectPath');
    const path = window.location.pathname;
    return path === '/contact' || path.endsWith('/contact') || redirectPath === '/contact';
  });
  const [showPrivacyPopup, setShowPrivacyPopup] = useState(() => {
    if (typeof window === 'undefined') return false;
    const redirectPath = sessionStorage.getItem('redirectPath');
    const path = window.location.pathname;
    return path === '/privacy' || path.endsWith('/privacy') || redirectPath === '/privacy';
  });
  const [loadMedia, setLoadMedia] = useState(false);

  // Handle GitHub Pages redirect scenario
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const redirectPath = sessionStorage.getItem('redirectPath');
    if (redirectPath) {
      sessionStorage.removeItem('redirectPath');
      
      if (redirectPath === '/contact') {
        setShowContactPopup(true);
      } else if (redirectPath === '/privacy') {
        setShowPrivacyPopup(true);
      }
      
      // Update the URL without page reload
      window.history.replaceState(null, '', redirectPath);
    }
  }, []);

  // DO NOT change language here - it's already set correctly in main.jsx
  // Changing it here causes flickering from English to target language
  
  // Track current language for re-renders only
  const [currentLang, setCurrentLang] = useState(i18n.language);

  useEffect(() => {
    const handleLanguageChange = (lng) => {
      setCurrentLang(lng);
    };

    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);



  // Update document metadata when language changes
  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const title = t('meta.title');
    const description = t('meta.description');
    const keywords = t('meta.keywords');

    // Update title
    document.title = title;

    // Update meta tags
    const updateMetaTag = (selector, content) => {
      const element = document.querySelector(selector);
      if (element) {
        element.setAttribute('content', content);
      }
    };

    updateMetaTag('meta[name="title"]', title);
    updateMetaTag('meta[name="description"]', description);
    updateMetaTag('meta[name="keywords"]', keywords);
    updateMetaTag('meta[property="og:title"]', title);
    updateMetaTag('meta[property="og:description"]', description);
    updateMetaTag('meta[property="twitter:title"]', title);
    updateMetaTag('meta[property="twitter:description"]', description);
  }, [t, i18n.language, currentLang]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    document.documentElement.classList.toggle('dark', theme === 'dark');
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme;
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';

    if (typeof window !== 'undefined') {
      window.__INITIAL_THEME__ = theme;
    }
  }, [theme, isRTL]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.__HAS_MANUAL_THEME__ = hasManualPreference;

    try {
      if (hasManualPreference) {
        window.localStorage.setItem(THEME_STORAGE_KEY, theme);
      } else {
        window.localStorage.removeItem(THEME_STORAGE_KEY);
      }
    } catch (error) {
      return;
    }
  }, [theme, hasManualPreference]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const syncThemeWithSystem = (event) => {
      if (hasManualPreference) {
        return;
      }
      setTheme(event.matches ? 'dark' : 'light');
    };

    if (!hasManualPreference) {
      setTheme(mediaQuery.matches ? 'dark' : 'light');
    }

    mediaQuery.addEventListener('change', syncThemeWithSystem);

    return () => mediaQuery.removeEventListener('change', syncThemeWithSystem);
  }, [hasManualPreference]);

  // Lazy load media on first scroll
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleScroll = () => {
      setLoadMedia(true);
      window.removeEventListener('scroll', handleScroll);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleToggleTheme = useCallback((event) => {
    if (typeof window !== 'undefined' && typeof window.checkCookieConsent === 'function') {
      if (!window.checkCookieConsent()) {
        if (typeof window.requestCookieConsentForTheme === 'function') {
          window.requestCookieConsentForTheme();
          
          window.onCookieConsentAccepted = () => {
            handleToggleTheme(event);
            window.onCookieConsentAccepted = null;
          };
        }
        return;
      }
    }

    const applyThemeChange = () => {
      flushSync(() => {
        setTheme((current) => {
          const nextTheme = current === 'dark' ? 'light' : 'dark';
          const systemTheme = getSystemPreference();

          if (nextTheme === systemTheme) {
            setHasManualPreference(false);
          } else {
            setHasManualPreference(true);
          }

          if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
            window.gtag('event', 'theme_toggle', {
              event_category: 'user_preference',
              event_label: nextTheme,
              is_system_preference: nextTheme === systemTheme
            });
          }

          return nextTheme;
        });
      });
    };

    if (!document.startViewTransition || !event) {
      applyThemeChange();
      return;
    }

    const x = event.clientX;
    const y = event.clientY;
    const endRadius = Math.hypot(
      Math.max(x, window.innerWidth - x),
      Math.max(y, window.innerHeight - y)
    );

    const transition = document.startViewTransition(applyThemeChange);

    transition.ready.then(() => {
      const clipPath = [
        `circle(0px at ${x}px ${y}px)`,
        `circle(${endRadius}px at ${x}px ${y}px)`,
      ];

      document.documentElement.animate(
        {
          clipPath: clipPath,
        },
        {
          duration: 500,
          easing: 'ease-in-out',
          pseudoElement: '::view-transition-new(root)',
        }
      );
    });
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.resetThemeToSystem = () => {
        const systemTheme = getSystemPreference();
        setTheme(systemTheme);
        setHasManualPreference(false);
      };
    }
    
    return () => {
      if (typeof window !== 'undefined') {
        window.resetThemeToSystem = null;
      }
    };
  }, []);

  const openContactPopup = () => {
    setShowContactPopup(true);
    const basePath = i18n.language === 'ro' ? '/ro' : '';
    window.history.pushState(null, '', `${basePath}/contact`);
  };
  
  const openPrivacyPopup = () => {
    setShowPrivacyPopup(true);
    const basePath = i18n.language === 'ro' ? '/ro' : '';
    window.history.pushState(null, '', `${basePath}/privacy`);
  };

  const features = [
    {
      icon: Zap,
      title: t('features.twoTap.title'),
      description: t('features.twoTap.description')
    },
    {
      icon: MapPin,
      title: t('features.smartTrip.title'),
      description: t('features.smartTrip.description')
    },
    {
      icon: Globe,
      title: t('features.globe.title'),
      description: t('features.globe.description')
    },
    {
      icon: Shield,
      title: t('features.privacy.title'),
      description: t('features.privacy.description')
    },
    {
      icon: Sparkles,
      title: t('features.customStyles.title'),
      description: t('features.customStyles.description')
    },
    {
      icon: Camera,
      title: t('features.manualControl.title'),
      description: t('features.manualControl.description')
    }
  ];

  const howItWorks = [
    {
      step: 1,
      icon: '/step1.svg',
      title: t('howItWorks.step1.title'),
      description: t('howItWorks.step1.description')
    },
    {
      step: 2,
      icon: '/step2.svg',
      title: t('howItWorks.step2.title'),
      description: t('howItWorks.step2.description')
    },
    {
      step: 3,
      icon: '/step3.svg',
      title: t('howItWorks.step3.title'),
      description: t('howItWorks.step3.description')
    }
  ];

  const screenshots = [
    { src: '/image1.webp', alt: t('screenshots.alt1') },
    { src: '/image2.webp', alt: t('screenshots.alt2') },
    { src: '/image3.webp', alt: t('screenshots.alt3') },
    { src: '/image4.webp', alt: t('screenshots.alt4') },
    { src: '/image5.webp', alt: t('screenshots.alt5') }
  ];

  const ContactPopup = ({ isOpen, onClose }) => {
    useEffect(() => {
      if (isOpen) {
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.documentElement.style.overflow = 'hidden';
        if (scrollbarWidth > 0) {
          document.documentElement.style.paddingRight = `${scrollbarWidth}px`;
          const header = document.querySelector('header');
          if (header) header.style.paddingRight = `${scrollbarWidth}px`;
        }
      } else {
        document.documentElement.style.overflow = '';
        document.documentElement.style.paddingRight = '';
        const header = document.querySelector('header');
        if (header) header.style.paddingRight = '';
      }
      return () => {
        document.documentElement.style.overflow = '';
        document.documentElement.style.paddingRight = '';
        const header = document.querySelector('header');
        if (header) header.style.paddingRight = '';
      };
    }, [isOpen]);

    if (!isOpen) return null;

    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
        onClick={onClose}
      >
        <div
          className="bg-white/70 dark:bg-slate-800/70 backdrop-blur-xl rounded-3xl shadow-2xl max-w-md w-full p-4 border-2 border-sky-200/50 dark:border-sky-700/50"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-gradient-to-r from-sky-600 to-blue-600 rounded-2xl mb-4 p-4 flex items-center justify-between">
            <h3 className="text-xl font-bold text-white drop-shadow-sm">
              {t('contact.title')}
            </h3>
            <button
              onClick={onClose}
              className="text-white hover:bg-white/20 rounded-full p-2 transition-all duration-200 hover:scale-105"
              aria-label={t('contact.close')}
            >
              <X size={24} />
            </button>
          </div>
          <div className="text-center mb-4 mt-8">
            <a 
              href="mailto:marian@qod.ee?subject=TripReel%20Suggestion" 
              className="inline-flex items-center gap-2 text-lg font-semibold text-sky-600 dark:text-sky-400 hover:text-sky-700 dark:hover:text-sky-300 transition-colors"
            >
              <Mail size={20} />
              marian@qod.ee
            </a>
          </div>
          <p className="text-slate-700 dark:text-slate-300 text-center">
            {t('contact.description')}
          </p>
        </div>
      </div>
    );
  };

  const PrivacyPopup = ({ isOpen, onClose }) => {
    useEffect(() => {
      if (isOpen) {
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.documentElement.style.overflow = 'hidden';
        if (scrollbarWidth > 0) {
          document.documentElement.style.paddingRight = `${scrollbarWidth}px`;
          const header = document.querySelector('header');
          if (header) header.style.paddingRight = `${scrollbarWidth}px`;
        }
      } else {
        document.documentElement.style.overflow = '';
        document.documentElement.style.paddingRight = '';
        const header = document.querySelector('header');
        if (header) header.style.paddingRight = '';
      }
      return () => {
        document.documentElement.style.overflow = '';
        document.documentElement.style.paddingRight = '';
        const header = document.querySelector('header');
        if (header) header.style.paddingRight = '';
      };
    }, [isOpen]);

    if (!isOpen) return null;

    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
        onClick={onClose}
      >
        <div
          className="bg-white/70 dark:bg-slate-800/70 backdrop-blur-xl rounded-3xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden border-2 border-sky-200/50 dark:border-sky-700/50"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-gradient-to-r from-sky-600 to-blue-600 rounded-2xl mx-4 my-4 p-4 flex items-center justify-between sticky top-0 z-10">
            <h3 className="text-xl font-bold text-white drop-shadow-sm">
              App Privacy Policy
            </h3>
            <button
              onClick={onClose}
              className="text-white hover:bg-white/20 rounded-full p-2 transition-all duration-200 hover:scale-105"
              aria-label={t('contact.close')}
            >
              <X size={24} />
            </button>
          </div>
          <div className="overflow-y-auto max-h-[calc(85vh-8rem)] px-6 pb-6">
            <div className="prose prose-sm sm:prose dark:prose-invert max-w-none pt-6">
              <p className="text-slate-600 dark:text-slate-400 mb-4">
                <strong>Last updated:</strong> August 1st, 2026
              </p>

              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mt-6 mb-3">1. Introduction</h3>
              <p className="text-slate-700 dark:text-slate-300 mb-4">
                This Privacy Policy applies to TripReel (the "App"), which creates beautiful travel videos automatically using your photos' location data. Your privacy is our top priority. This policy explains how information is handled when you use the App.
              </p>

              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mt-6 mb-3">2. Data Collection and Use</h3>
              <div className="bg-sky-500/20 dark:bg-slate-700/50 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-slate-900 dark:text-white mb-2">📍 Photo Location Data Only</h4>
                <p className="text-slate-700 dark:text-slate-300 text-sm mb-2">
                  The App only reads the location metadata (GPS coordinates) from your photos:
                </p>
                <ul className={`list-disc ${isRTL ? 'pr-6' : 'pl-6'} text-slate-700 dark:text-slate-300 text-sm space-y-1`}>
                  <li>We do NOT access, view, store, or transmit any photo content or images</li>
                  <li>All processing happens offline on your device</li>
                  <li>None of your data leaves your device or is sent to any server</li>
                </ul>
              </div>

              <div className="bg-green-500/20 dark:bg-green-900/20 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-slate-900 dark:text-white mb-2">What We Do NOT Collect</h4>
                <ul className="list-none text-slate-700 dark:text-slate-300 text-sm space-y-2">
                  <li>✅ No personal information such as names, email addresses, or any other identifying information</li>
                  <li>✅ No user accounts or registration required</li>
                  <li>✅ No tracking of individual user behavior</li>
                  <li>✅ No sharing of data with third parties</li>
                </ul>
              </div>

              <div className="bg-amber-500/20 dark:bg-amber-900/20 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-slate-900 dark:text-white mb-2">✈️ Airplane Mode Test</h4>
                <p className="text-slate-700 dark:text-slate-300 text-sm">
                  You can verify our privacy commitment by using the App in airplane mode - it works completely offline!
                </p>
              </div>

              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mt-6 mb-3">3. Third-Party Services</h3>
              <div className="bg-purple-500/20 dark:bg-purple-900/20 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-slate-900 dark:text-white mb-2">📱 Google AdMob</h4>
                <p className="text-slate-700 dark:text-slate-300 text-sm mb-2">
                  The App displays advertisements using Google AdMob:
                </p>
                <ul className={`list-disc ${isRTL ? 'pr-6' : 'pl-6'} text-slate-700 dark:text-slate-300 text-sm space-y-1`}>
                  <li><strong>Premium Users:</strong> If you have a premium paid plan, no advertisements are shown and no data collection occurs through AdMob. The app is fully free of third-party data collection in this case.</li>
                  <li>While the App itself does not collect personal data, Google AdMob may collect and use certain information from your device to serve ads. This may include device identifiers and other information not linked to your identity, as described in Google's privacy policies.</li>
                  <li>AdMob may use cookies and similar technologies to collect data about your device and usage patterns for advertising purposes.</li>
                </ul>
                <p className="text-slate-700 dark:text-slate-300 text-sm mt-2">
                  <strong>What You Should Know:</strong> Third-party ad networks like AdMob may collect, use, and share data as described in their own privacy policies. For more information, please refer to the <a href="https://policies.google.com/privacy" className="text-sky-600 dark:text-sky-400 hover:underline">Google Privacy Policy</a> and <a href="https://support.google.com/admob/answer/6128543" className="text-sky-600 dark:text-sky-400 hover:underline">AdMob Policies</a>.
                </p>
              </div>

              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mt-6 mb-3">4. Permissions</h3>
              <div className="bg-blue-500/20 dark:bg-blue-900/20 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-slate-900 dark:text-white mb-2">📸 Photo Library Access</h4>
                <p className="text-slate-700 dark:text-slate-300 text-sm mb-2">
                  The App requests access to your photo library solely to read location metadata from photos. This permission is required for the App to function and create your trip timeline videos.
                </p>
                <p className="text-slate-700 dark:text-slate-300 text-sm">
                  <strong>What We Do NOT Access:</strong>
                </p>
                <ul className="list-none text-slate-700 dark:text-slate-300 text-sm space-y-1 mt-1">
                  <li>❌ Photo content or images</li>
                  <li>❌ Any data other than location coordinates and timestamps</li>
                  <li>❌ Contacts, messages, or other personal information</li>
                </ul>
              </div>

              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mt-6 mb-3">5. Usage Data Shared with Apple</h3>
              <p className="text-slate-700 dark:text-slate-300 mb-4">
                To help us understand how the App is performing and where it can be improved, basic usage information may be shared between the developer and Apple through Apple's own developer platforms. This data is limited strictly to how the App itself is used (telemetry information), not the content of what you're doing. And this can always be verified by anyone using Apple's App Privacy Report. This exchange happens directly between the developer and Apple, no outside third party is involved, and the data is never sold or passed on to anyone else. By using the App, you agree to this usage data being shared with Apple in this way.
              </p>

              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mt-6 mb-3">Contact</h3>
              <p className="text-slate-700 dark:text-slate-300 mb-4">
                Questions about this privacy policy? <button onClick={() => { onClose(); openContactPopup(); }} className="text-sky-600 dark:text-sky-400 hover:underline">Contact page</button>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      <Header theme={theme} onToggleTheme={handleToggleTheme} />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-sky-100 dark:bg-sky-900/30 rounded-full text-sky-700 dark:text-sky-300 text-sm font-medium">
                <Sparkles className="w-4 h-4" />
                <span>{t('hero.badge')}</span>
              </div>
              
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight text-balance">
                <span className="bg-gradient-to-r from-sky-600 via-blue-600 to-indigo-600 dark:from-sky-400 dark:via-blue-400 dark:to-indigo-400 bg-clip-text text-transparent">
                  {t('hero.title1')}
                </span>
                <br />
                <span className="text-slate-900 dark:text-white">
                  {t('hero.title2')}
                </span>
              </h1>
              
              <p className="text-xl text-slate-600 dark:text-slate-300 leading-relaxed max-w-2xl">
                {t('hero.description')}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 items-start">
                <AppStoreBadges />
              </div>
              
              <div className="flex items-center gap-8 pt-4 text-sm text-slate-600 dark:text-slate-400">
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-green-600 dark:text-green-400" />
                  <span>{t('hero.secure')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                  <span>{t('hero.worksOffline')}</span>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="relative z-10 animate-float">
                <ResponsiveImage 
                  src="/earth_transparent.webp" 
                  alt={t('hero.globeAlt')} 
                  className="w-3/5 lg:w-full h-auto opacity-90 mx-auto lg:mx-0"
                  width={1161}
                  height={1162}
                  fetchpriority="high"
                  loading="eager"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-br from-sky-400/20 to-blue-600/20 dark:from-sky-500/30 dark:to-blue-700/30 blur-3xl animate-pulse"></div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 px-4 sm:px-6 lg:px-8 bg-white/50 dark:bg-slate-800/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 dark:text-white mb-4">
              {t('howItWorks.title')}
            </h2>
            <p className="text-xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
              {t('howItWorks.subtitle')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 relative">
            {/* Connection lines for desktop */}
            <div className="hidden md:block absolute top-1/4 left-0 right-0 h-0.5 bg-gradient-to-r from-sky-200 via-blue-300 to-indigo-200 dark:from-sky-800 dark:via-blue-700 dark:to-indigo-800 -z-10"></div>
            
            {howItWorks.map((item, index) => (
              <div key={index} className="relative">
                <div className="bg-white dark:bg-slate-800 rounded-2xl p-8 shadow-xl border border-slate-200 dark:border-slate-700 overflow-visible">
                  <div className="absolute -top-6 left-1/2 -translate-x-1/2">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-sky-500 to-blue-600 dark:from-sky-400 dark:to-blue-500 flex items-center justify-center text-white font-bold text-xl shadow-lg">
                      {item.step}
                    </div>
                  </div>
                  
                  <div className="mt-8 text-center">
                    <img src={loadMedia ? item.icon : undefined} alt={`${item.title} icon`} className="h-60 w-auto p-6 mb-4 block mx-auto drop-shadow-[0_20px_40px_rgba(0,0,0,0.1)] dark:drop-shadow-[0_35px_70px_rgba(255,255,255,0.25)]" />
                    
                    <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3">
                      {item.title}
                    </h3>
                    <p className="text-slate-600 dark:text-slate-300">
                      {item.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Video Example Section */}
      <section id="video" className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-900" style={{ scrollMarginTop: '4rem' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              {t('videoSection.title')}
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              {t('videoSection.subtitle')}
            </p>
          </div>
          
          <div className="max-w-64 sm:max-w-72 mx-auto" style={{ maxWidth: 'calc(61.6vh * 886 / 1920)' }}>
            <div className="relative bg-slate-800 rounded-[47px] p-0.5 shadow-2xl border border-slate-700">
              <div className="aspect-[886/1920] bg-black rounded-[44px] overflow-hidden" style={{ maxHeight: '61.6vh' }}>
                <video 
                  key="tripreel-demo"
                  className="w-full h-full object-contain"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="none"
                  src={loadMedia ? "/video_example.mp4" : undefined}
                >
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
            <p className="text-center text-slate-400 mt-3 text-xs">
              {t('videoSection.caption')}
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 dark:text-white mb-4">
              {t('features.title')}
            </h2>
            <p className="text-xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
              {t('features.subtitle')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <FeatureCard
                key={index}
                icon={feature.icon}
                title={feature.title}
                description={feature.description}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Screenshots Section */}
      <section id="the-app" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 dark:text-white mb-4">
              {t('screenshots.title')}
            </h2>
            <p className="text-xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
              {t('screenshots.subtitle')}
            </p>
          </div>
          
          {/* Desktop/Tablet: Auto-fit grid */}
          <div className="hidden md:grid md:grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-4">
            {screenshots.map((screenshot, index) => (
              <div
                key={index}
                className="group relative bg-white dark:bg-slate-800 rounded-[47px] p-0.5 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 border border-slate-200 dark:border-slate-700"
              >
                <div className="aspect-[9/19.5] bg-slate-100 dark:bg-slate-900 rounded-[44px] overflow-hidden">
                  <img
                    src={screenshot.src}
                    alt={screenshot.alt}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Mobile: Horizontal scroll */}
          <div className="md:hidden overflow-x-auto pb-4">
            <div className="flex gap-4 px-4" style={{ width: 'max-content' }}>
              {screenshots.map((screenshot, index) => (
                <div
                  key={index}
                  className="group relative bg-white dark:bg-slate-800 rounded-[47px] p-0.5 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 border border-slate-200 dark:border-slate-700 flex-shrink-0"
                  style={{ width: '260px' }}
                >
                  <div className="aspect-[9/19.5] bg-slate-100 dark:bg-slate-900 rounded-[44px] overflow-hidden">
                    <img
                      src={screenshot.src}
                      alt={screenshot.alt}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <Suspense fallback={<div className="py-20 text-center"><div className="inline-block w-8 h-8 border-4 border-sky-200 border-t-sky-500 rounded-full animate-spin"></div></div>}>
        <FAQ />
      </Suspense>

      {/* Video Ideas Section */}
      <Suspense fallback={<div className="py-20 text-center"><div className="inline-block w-8 h-8 border-4 border-sky-200 border-t-sky-500 rounded-full animate-spin"></div></div>}>
        <VideoIdeas />
      </Suspense>

      {/* About Section */}
      <section id="about" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50 dark:bg-slate-900">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 dark:text-white mb-4">
              {t('about.title')}
            </h2>
            <p className="text-xl text-slate-600 dark:text-sky-400 font-semibold">
              {t('about.subtitle')}
            </p>
          </div>

          {/* How It Works */}
          <div className="mb-16 bg-white dark:bg-slate-800 rounded-2xl p-8 shadow-lg">
            <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
              {t('about.howItWorks.title')}
            </h3>
            <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
              {t('about.howItWorks.description')}
            </p>
          </div>

          {/* Customization */}
          <div className="mb-16 bg-white dark:bg-slate-800 rounded-2xl p-8 shadow-lg">
            <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
              {t('about.customization.title')}
            </h3>
            <p className="text-slate-700 dark:text-slate-300 mb-6">
              {t('about.customization.description')}
            </p>
            <ul className="space-y-3">
              <li className="flex items-start">
                <span className="text-sky-500 mr-2 mt-1">•</span>
                <span className="text-slate-700 dark:text-slate-300">{t('about.customization.features.videoQuality')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-sky-500 mr-2 mt-1">•</span>
                <span className="text-slate-700 dark:text-slate-300">{t('about.customization.features.bitrate')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-sky-500 mr-2 mt-1">•</span>
                <span className="text-slate-700 dark:text-slate-300">{t('about.customization.features.earthStyles')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-sky-500 mr-2 mt-1">•</span>
                <span className="text-slate-700 dark:text-slate-300">{t('about.customization.features.borders')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-sky-500 mr-2 mt-1">•</span>
                <span className="text-slate-700 dark:text-slate-300">{t('about.customization.features.aspectRatios')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-sky-500 mr-2 mt-1">•</span>
                <span className="text-slate-700 dark:text-slate-300">{t('about.customization.features.tripSpeed')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-sky-500 mr-2 mt-1">•</span>
                <span className="text-slate-700 dark:text-slate-300">{t('about.customization.features.pauseDuration')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-sky-500 mr-2 mt-1">•</span>
                <span className="text-slate-700 dark:text-slate-300">{t('about.customization.features.locationLabels')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-sky-500 mr-2 mt-1">•</span>
                <span className="text-slate-700 dark:text-slate-300">{t('about.customization.features.pathDisplay')}</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="relative bg-gradient-to-br from-sky-500 via-blue-600 to-indigo-600 dark:from-sky-600 dark:via-blue-700 dark:to-indigo-700 rounded-3xl p-12 shadow-2xl overflow-hidden">
            <div className="absolute inset-0 bg-[url('/earth_transparent.webp')] opacity-10 bg-cover bg-center"></div>
            
            <div className="relative z-10 text-center space-y-8">
              <h2 className="text-4xl sm:text-5xl font-bold text-white">
                {t('cta.title')}
              </h2>
              <p className="text-xl text-blue-50 max-w-2xl mx-auto">
                {t('cta.description')}
              </p>
              
              <div className="pt-4">
                <AppStoreBadges />
              </div>
              
              <p className="text-sm text-blue-100 pt-4">
                {t('cta.features')}
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer openContactPopup={openContactPopup} openPrivacyPopup={openPrivacyPopup} />

      <ContactPopup
        isOpen={showContactPopup}
        onClose={() => {
          setShowContactPopup(false);
          const basePath = i18n.language === 'ro' ? '/ro' : '';
          window.history.pushState(null, '', basePath || '/');
        }}
      />

      <PrivacyPopup
        isOpen={showPrivacyPopup}
        onClose={() => {
          setShowPrivacyPopup(false);
          const basePath = i18n.language === 'ro' ? '/ro' : '';
          window.history.pushState(null, '', basePath || '/');
        }}
      />
      <StructuredData />
    </div>
  );
}

export default App;
